// Push Notification Utilities for Song Requests
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';

/**
 * Sends via the authenticated `sendPushNotification` callable. This used to POST
 * to an open HTTP endpoint, which let anyone on the internet push arbitrary
 * notifications to any subscription they could name.
 */
const callSendPushNotification = async (subscription, payload) => {
  const callable = httpsCallable(functions, 'sendPushNotification');
  try {
    const result = await callable({ subscription, payload });
    return result?.data?.success !== false;
  } catch (error) {
    const message =
      error?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?\s*$/, '').trim() ||
      error?.code ||
      'Failed to send push notification';
    const err = new Error(message);
    err.code = error?.code;
    err.cause = error;
    throw err;
  }
};


/**
 * Get an artist's push subscription from the database
 * @param {string} artistId - The artist's user ID
 * @returns {Object|null} The push subscription object or null if not found
 */
export const getArtistPushSubscription = async (artistId) => {
  try {
    console.log('🔍 DEBUG: Looking for subscription for artistId:', artistId);
    
    // ✅ PRIMARY: Query Firestore database for the stored subscription
    try {
      console.log('🔍 DEBUG: Attempting Firestore query for collection: push_subscriptions, document:', artistId);
      console.log('🔍 DEBUG: Using Firestore instance:', !!db);
      console.log('🔍 DEBUG: Collection path: push_subscriptions');
      console.log('🔍 DEBUG: Document ID:', artistId);
      
      // Test if we can read from other collections to isolate the issue
      try {
        console.log('🔍 DEBUG: Testing Firestore access by reading profiles collection...');
        const testDoc = await getDoc(doc(db, 'profiles', artistId));
        console.log('🔍 DEBUG: Profiles collection test result:', {
          exists: testDoc.exists(),
          hasData: !!testDoc.data(),
          dataKeys: testDoc.exists() ? Object.keys(testDoc.data()) : []
        });
      } catch (testError) {
        console.log('🔍 DEBUG: Profiles collection test failed:', testError);
      }
      
      // Test if we can list documents in push_subscriptions collection
      try {
        console.log('🔍 DEBUG: Testing Firestore access by listing push_subscriptions collection...');
        const { collection, getDocs } = await import('firebase/firestore');
        const querySnapshot = await getDocs(collection(db, 'push_subscriptions'));
        console.log('🔍 DEBUG: Push subscriptions collection test result:', {
          size: querySnapshot.size,
          documentIds: querySnapshot.docs.map(doc => doc.id)
        });
      } catch (listError) {
        console.log('🔍 DEBUG: Push subscriptions collection listing failed:', listError);
      }
      
      const subscriptionDoc = await getDoc(doc(db, 'push_subscriptions', artistId));
      console.log('🔍 DEBUG: Firestore query result:', {
        exists: subscriptionDoc.exists(),
        hasData: !!subscriptionDoc.data(),
        dataKeys: subscriptionDoc.exists() ? Object.keys(subscriptionDoc.data()) : []
      });
      
      if (subscriptionDoc.exists()) {
        const data = subscriptionDoc.data();
        console.log('✅ Found subscription in Firestore for user:', artistId);
        
        // Reconstruct the subscription object from stored data
        const storedSubscription = data.subscription;
        if (storedSubscription && storedSubscription.endpoint) {
          // Create a subscription-like object that web-push can use
          const reconstructedSubscription = {
            endpoint: storedSubscription.endpoint,
            keys: {
              p256dh: storedSubscription.keys?.p256dh,
              auth: storedSubscription.keys?.auth
            },
            expirationTime: storedSubscription.expirationTime
          };
          console.log('✅ Subscription reconstructed from Firestore:', {
            endpoint: reconstructedSubscription.endpoint?.substring(0, 50) + '...',
            hasKeys: !!reconstructedSubscription.keys,
            hasP256dh: !!reconstructedSubscription.keys?.p256dh,
            hasAuth: !!reconstructedSubscription.keys?.auth
          });
          return reconstructedSubscription;
        } else {
          console.log('⚠️ Subscription data exists but is invalid:', {
            hasStoredSubscription: !!storedSubscription,
            hasEndpoint: !!storedSubscription?.endpoint,
            storedSubscriptionKeys: storedSubscription ? Object.keys(storedSubscription) : []
          });
        }
      } else {
        console.log('⚠️ No Firestore document found for user:', artistId);
      }
    } catch (dbError) {
      console.error('❌ Firestore query failed with error:', dbError);
      console.warn('⚠️ Firestore query failed, falling back to localStorage:', dbError);
    }
    
    // 🔄 FALLBACK: Check localStorage (for backward compatibility)
    const storedSubscription = localStorage.getItem(`ysr_push_subscription_${artistId}`);
    if (storedSubscription) {
      console.log('✅ Found stored subscription in localStorage for user:', artistId);
      return JSON.parse(storedSubscription);
    }

    // 🔄 FALLBACK: Check for subscription without user ID prefix
    const fallbackSubscription = localStorage.getItem('ysr_push_subscription');
    if (fallbackSubscription) {
      console.log('✅ Found fallback subscription in localStorage');
      return JSON.parse(fallbackSubscription);
    }

    console.log('❌ No subscription found for user:', artistId);
    console.log('🔍 Available localStorage keys:', Object.keys(localStorage).filter(key => key.includes('subscription')));

    return null;
  } catch (error) {
    console.error('❌ Error getting artist push subscription:', error);
    return null;
  }
};

/**
 * Store a user's push subscription in the database
 * @param {string} userId - The user's ID
 * @param {Object} subscription - The push subscription object
 */
/** Binary key material from PushSubscription → base64 string for Firestore */
function encodePushKeyPart(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  let bytes;
  if (v instanceof ArrayBuffer) {
    bytes = new Uint8Array(v);
  } else if (ArrayBuffer.isView(v)) {
    bytes = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  } else {
    return null;
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export const storePushSubscription = async (userId, subscription) => {
  try {
    console.log('🔍 DEBUG: Starting to store push subscription for user:', userId);
    console.log('🔍 DEBUG: Subscription data:', {
      hasEndpoint: !!subscription?.endpoint,
      hasKeys: !!subscription?.keys,
      endpointLength: subscription?.endpoint?.length || 0
    });
    
    // Deep debug the subscription object
    console.log('🔍 DEBUG: Full subscription object:', subscription);
    console.log('🔍 DEBUG: Subscription keys:', subscription?.keys);
    console.log('🔍 DEBUG: Keys type:', typeof subscription?.keys);
    console.log('🔍 DEBUG: Available properties:', Object.getOwnPropertyNames(subscription || {}));
    
    if (subscription?.keys) {
      console.log('🔍 DEBUG: Keys object properties:', Object.getOwnPropertyNames(subscription.keys));
      console.log('🔍 DEBUG: p256dh:', subscription.keys.p256dh);
      console.log('🔍 DEBUG: auth:', subscription.keys.auth);
    }
    
    // ✅ PRIMARY: Store in Firestore database
    let firestoreSuccess = false;
    try {
      // Extract only the serializable properties from PushSubscription
      let p256dh = encodePushKeyPart(subscription.keys?.p256dh);
      let auth = encodePushKeyPart(subscription.keys?.auth);
      
      // Try alternative ways to get the keys if they're missing
      if (!p256dh || !auth) {
        console.log('🔍 DEBUG: Keys missing, trying alternative methods...');
        
        // Method 1: Try toJSON if available
        if (subscription.toJSON) {
          try {
            const jsonSub = subscription.toJSON();
            console.log('🔍 DEBUG: toJSON result:', jsonSub);
            if (jsonSub.keys) {
              p256dh = p256dh || jsonSub.keys.p256dh;
              auth = auth || jsonSub.keys.auth;
            }
          } catch (e) {
            console.log('🔍 DEBUG: toJSON failed:', e);
          }
        }
        
        // Method 2: Try accessing keys as array buffer properties
        if (!p256dh && subscription.keys) {
          const keysProps = Object.getOwnPropertyNames(subscription.keys);
          console.log('🔍 DEBUG: Available key properties:', keysProps);
          
          // Look for properties that might contain the key data
          for (const prop of keysProps) {
            const value = subscription.keys[prop];
            if (value && typeof value === 'object' && value.byteLength) {
              console.log('🔍 DEBUG: Found potential key in property:', prop, value);
              if (prop.includes('256') || prop.includes('p256')) {
                p256dh = value;
              } else if (prop.includes('auth')) {
                auth = value;
              }
            }
          }
        }
      }

      p256dh = encodePushKeyPart(p256dh);
      auth = encodePushKeyPart(auth);
      
      const serializableSubscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: p256dh,
          auth: auth
        },
        expirationTime: subscription.expirationTime
      };
      
      // Validate that required keys exist before storing
      if (!serializableSubscription.keys.p256dh || !serializableSubscription.keys.auth) {
        throw new Error(`Missing required subscription keys: p256dh=${!!serializableSubscription.keys.p256dh}, auth=${!!serializableSubscription.keys.auth}`);
      }
      
      console.log('🔍 DEBUG: Attempting to store in Firestore collection: push_subscriptions, document:', userId);
      console.log('🔍 DEBUG: Validated subscription data:', {
        hasEndpoint: !!serializableSubscription.endpoint,
        hasP256dh: !!serializableSubscription.keys.p256dh,
        hasAuth: !!serializableSubscription.keys.auth,
        endpointLength: serializableSubscription.endpoint?.length || 0,
        p256dhLength: serializableSubscription.keys.p256dh?.length || 0,
        authLength: serializableSubscription.keys.auth?.length || 0,
        endpoint: serializableSubscription.endpoint?.substring(0, 50) + '...',
        p256dh: serializableSubscription.keys.p256dh ? 'Present' : 'Missing',
        auth: serializableSubscription.keys.auth ? 'Present' : 'Missing'
      });
      
      await setDoc(doc(db, 'push_subscriptions', userId), {
        subscription: serializableSubscription,
        userId: userId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      console.log('✅ Push subscription stored in Firestore for user:', userId);
      firestoreSuccess = true;
    } catch (dbError) {
      console.error('❌ Firestore storage failed with error:', dbError);
      console.warn('⚠️ Firestore storage failed, falling back to localStorage only');
    }
    
    // 🔄 FALLBACK: Also store locally for backward compatibility
    localStorage.setItem(`ysr_push_subscription_${userId}`, JSON.stringify(subscription));
    console.log('✅ Push subscription stored in localStorage for user:', userId);
    
    if (firestoreSuccess) {
      console.log('✅ Push subscription stored successfully in both Firestore and localStorage for user:', userId);
      return true;
    } else {
      console.warn('⚠️ Push subscription stored in localStorage only (Firestore failed) for user:', userId);
      return false;
    }
  } catch (error) {
    console.error('❌ Error storing push subscription:', error);
    return false;
  }
};

/**
 * Remove a user's push subscription
 * @param {string} userId - The user's ID
 */
export const removePushSubscription = async (userId) => {
  try {
    // ✅ PRIMARY: Remove from Firestore database
    try {
      await deleteDoc(doc(db, 'push_subscriptions', userId));
      console.log('✅ Push subscription removed from Firestore for user:', userId);
    } catch (dbError) {
      console.warn('⚠️ Firestore removal failed, falling back to localStorage only:', dbError);
    }
    
    // 🔄 FALLBACK: Also remove locally
    localStorage.removeItem(`ysr_push_subscription_${userId}`);

    console.log('✅ Push subscription removed for user:', userId);
    return true;
  } catch (error) {
    console.error('❌ Error removing push subscription:', error);
    return false;
  }
};

/**
 * Test function to send a test notification
 * @param {string} userId - The user's ID
 */


/**
 * Send a push notification using a subscription object
 * @param {Object} subscription - The push subscription object
 * @param {Object} notificationData - The notification data
 * @returns {Promise<boolean>} Success status
 */
export const sendPushNotification = async (subscription, notificationData) => {
  try {
    console.log('📱 Sending push notification:', notificationData);
    
    // Prepare the notification payload
    const notificationPayload = {
      title: notificationData.title,
      body: notificationData.body,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      data: notificationData.data || {},
      actions: notificationData.actions || [
        {
          action: 'view',
          title: 'View',
          icon: '/icons/icon-72x72.png'
        }
      ],
      requireInteraction: notificationData.requireInteraction !== false,
      tag: notificationData.tag || 'notification'
    };

    console.log('📱 Notification payload prepared:', notificationPayload);

    const ok = await callSendPushNotification(subscription, notificationPayload);
    console.log('✅ Push notification sent successfully');
    return ok;
  } catch (error) {
    console.error('❌ Error sending push notification:', error);
    return false;
  }
};

/**
 * Test function using Firebase Cloud Functions directly
 * @param {string} userId - The user's ID
 */
export const sendTestNotificationDirect = async (userId) => {
  console.log('🧪 Sending test notification directly to user:', userId);

  const artistSubscription = await getArtistPushSubscription(userId);

  if (!artistSubscription) {
    console.log('❌ No push subscription found for user:', userId);
    throw new Error('No push subscription on this account — enable notifications first.');
  }

  const testPayload = {
    title: 'Test Notification! 🧪',
    body: 'This is a test push notification to verify your subscription is working.',
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    data: {
      type: 'test',
      timestamp: Date.now(),
      url: '/admin/live-dashboard'
    },
    actions: [
      {
        action: 'view',
        title: 'View',
        icon: '/icons/icon-72x72.png'
      }
    ],
    requireInteraction: true,
    tag: 'test-notification'
  };

  console.log('📱 Test notification payload prepared:', testPayload);

  const ok = await callSendPushNotification(artistSubscription, testPayload);
  console.log('✅ Test push notification sent successfully');
  return ok;
};

